import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Briefcase, Lightbulb, DollarSign, ClipboardList, Calculator, ShoppingCart,
  TrendingDown, Clock, Layers, BarChart3, Scale, Truck, ArrowRight, Check,
} from 'lucide-react';

/**
 * Onboarding — role → challenge → personalized result.
 *
 * Flow + copy + layout mirror the Jobotics prototype (jobotics-prototype):
 *   Step 1  pick your role        (6 option cards)
 *   Step 2  pick your challenge   (6 option cards)
 *   Step 3  personalized greeting + recommended-agents nudge → enter app
 *
 * The prototype collects {role, challenge}; our /api/auth/profile contract
 * wants {displayName, jobTitle, discipline}. We bridge: role → jobTitle slug,
 * discipline defaults to MEP (the demo's scope), displayName falls back to the
 * user's existing name / email prefix. The challenge drives the step-3 copy
 * (client-side), exactly as the prototype uses it.
 *
 * Reached via the "Tell us your role" nudge on Home or direct /onboarding.
 * Onboarding is optional — every step can be skipped.
 */

// Step 1 — roles. `job` maps to our JOB_TITLES slugs (data/agentCatalog.js).
const ROLES = [
  { id: 'project-executive', label: 'Project Executive', sub: 'Oversees delivery & outcomes', icon: Briefcase,     job: 'owner_exec' },
  { id: 'vp-innovation',     label: 'VP of Innovation',  sub: 'Drives tech & process change', icon: Lightbulb,     job: 'bim_vdc' },
  { id: 'cfo',               label: 'CFO / Finance Lead', sub: 'Owns budgets & cost control',  icon: DollarSign,    job: 'owner_exec' },
  { id: 'project-manager',   label: 'Project Manager',    sub: 'Runs day-to-day execution',    icon: ClipboardList, job: 'project_manager' },
  { id: 'estimator',         label: 'Estimator',          sub: 'Builds bids & quantity takeoffs', icon: Calculator, job: 'estimator' },
  { id: 'procurement',       label: 'Procurement Lead',   sub: 'Manages vendors & materials',  icon: ShoppingCart,  job: 'procurement' },
];

// Step 2 — challenges.
const CHALLENGES = [
  { id: 'cost-overruns',   label: 'Cost overruns',         sub: 'Margin erodes before I see it',   icon: TrendingDown },
  { id: 'schedule-delays', label: 'Schedule delays',       sub: 'The critical path keeps slipping', icon: Clock },
  { id: 'data-fragmented', label: 'Fragmented data',       sub: 'Answers live across too many systems', icon: Layers },
  { id: 'reporting',       label: 'Reporting & visibility', sub: 'Status takes too long to assemble', icon: BarChart3 },
  { id: 'contract-risk',   label: 'Contract risk',         sub: 'Exposure hides in the terms',     icon: Scale },
  { id: 'procurement',     label: 'Procurement',           sub: 'Vendors & lead times bite late',  icon: Truck },
];

// Step 3 — concise, role-aware personalization line (challenge flavors the noun).
function personalize(role, challenge) {
  const r = ROLES.find((x) => x.id === role);
  const c = CHALLENGES.find((x) => x.id === challenge);
  const who = r ? r.label : 'there';
  const focus = c ? c.label.toLowerCase() : 'your priorities';
  return {
    greeting: r ? `${r.label}.` : 'Welcome.',
    sub: `Based on ${who.toLowerCase() === 'there' ? 'your role' : 'your work'} and a focus on ${focus}, we've put the most relevant agents at the top of your home view.`,
  };
}

const TOTAL_STEPS = 3;

export default function Onboarding() {
  const { user, saveProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);            // 1 role · 2 challenge · 3 result
  const [role, setRole] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const finish = async (selectedRole) => {
    if (submitting) return;
    setSubmitting(true);
    const r = ROLES.find((x) => x.id === (selectedRole || role));
    // Mark this session as onboarded BEFORE saving. The App.js gate honors
    // this flag, so even if the profile save fails (backend hiccup, transient
    // 500, etc.) the user is NEVER trapped on this screen — they still reach
    // the app. A successful save also persists profileCompletedAt so future
    // logins skip onboarding entirely.
    try { if (user && user.id != null) sessionStorage.setItem('jb_onboarded_' + user.id, '1'); } catch (_) { /* private mode */ }
    await saveProfile({
      displayName: (user?.displayName || user?.name || user?.email?.split('@')[0] || 'User').trim().slice(0, 120),
      jobTitle: r ? r.job : 'other',
      discipline: 'mep_combined', // demo scope is MEP; profile edit can refine later
    }); // best-effort — saveProfile surfaces its own error toast on failure
    setSubmitting(false);
    navigate('/', { replace: true });
  };

  const pickRole = (id) => { setRole(id); setStep(2); };
  const pickChallenge = (id) => { setChallenge(id); setStep(3); };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i + 1 === step ? 'w-8 bg-sylvan-500' : i + 1 < step ? 'w-8 bg-sylvan-300' : 'w-4 bg-hairline-strong'
              }`}
            />
          ))}
        </div>

        {/* ── Step 1 · role ──────────────────────────── */}
        {step === 1 && (
          <section className="animate-fade-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-sylvan-50 text-sylvan-500 mb-4">
                <Briefcase className="w-5 h-5" />
              </div>
              <h1 className="text-3xl text-ink font-normal tracking-tight">What's your role at the company?</h1>
              <p className="text-ink-body mt-2">We'll show you what's most relevant to your work.</p>
            </div>
            <OptionGrid options={ROLES} onPick={pickRole} selectedId={role} />
            <div className="text-center mt-8">
              <button
                type="button"
                onClick={() => finish()}
                disabled={submitting}
                className="text-sm text-ink-muted hover:text-ink transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving…' : 'Skip this step'}
              </button>
            </div>
          </section>
        )}

        {/* ── Step 2 · challenge ─────────────────────── */}
        {step === 2 && (
          <section className="animate-fade-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-sylvan-50 text-sylvan-500 mb-4">
                <TrendingDown className="w-5 h-5" />
              </div>
              <h1 className="text-3xl text-ink font-normal tracking-tight">What's your biggest challenge right now?</h1>
              <p className="text-ink-body mt-2">We'll lead with the agents that move the needle on it.</p>
            </div>
            <OptionGrid options={CHALLENGES} onPick={pickChallenge} selectedId={challenge} />
            <div className="text-center mt-8">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-sm text-ink-muted hover:text-ink transition-colors"
              >
                ← Back
              </button>
            </div>
          </section>
        )}

        {/* ── Step 3 · personalized result ───────────── */}
        {step === 3 && (() => {
          const { greeting, sub } = personalize(role, challenge);
          return (
            <section className="animate-fade-in text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sylvan-500 text-white mb-5">
                <Check className="w-6 h-6" />
              </div>
              <h1 className="text-3xl text-ink font-normal tracking-tight">{greeting}</h1>
              <p className="text-ink-body mt-3 max-w-md mx-auto leading-relaxed">{sub}</p>
              <button
                type="button"
                onClick={() => finish()}
                disabled={submitting}
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-sylvan-500 hover:bg-sylvan-600 text-white font-medium px-6 py-3 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Setting up…' : 'Enter Jobotics'}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="text-sm text-ink-muted hover:text-ink transition-colors"
                >
                  ← Back
                </button>
              </div>
            </section>
          );
        })()}
      </div>
    </div>
  );
}

/* Shared option-card grid — icon + label + sub, hairline cards on cream,
   orange ring on hover/active. Mirrors the prototype's .option-card-grid. */
function OptionGrid({ options, onPick, selectedId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {options.map((o) => {
        const Icon = o.icon;
        const selected = o.id === selectedId;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onPick(o.id)}
            className={`group flex items-center gap-3 text-left rounded-lg border bg-white px-4 py-4 transition-all ${
              selected
                ? 'border-sylvan-500 ring-1 ring-sylvan-500'
                : 'border-hairline hover:border-sylvan-300 hover:bg-canvas-soft'
            }`}
          >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-canvas-soft text-ink-muted group-hover:text-sylvan-500 transition-colors shrink-0">
              <Icon className="w-5 h-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-ink font-medium leading-tight">{o.label}</span>
              <span className="block text-ink-muted text-sm mt-0.5 truncate">{o.sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
